import type { CSSProperties, ReactNode } from "react";

/** Where the logo sits when the box is wider than the mark. */
export type IconsLogoAlign = "left" | "center" | "right" | "spread";

// Literal classes, not computed — Tailwind's JIT can't see `justify-${align}`.
const justifyClassFor: Record<IconsLogoAlign, string> = {
  left: "justify-start",
  center: "justify-center",
  right: "justify-end",
  spread: "justify-between",
};

interface IconsLogoBoxProps {
  align?: IconsLogoAlign | undefined;
  /** Fixed box in px, so every logo occupies the same slot. */
  width?: number | undefined;
  height?: number | undefined;
  /** 12px between the mark and a label sitting beside it. */
  labelGap?: boolean | undefined;
  /** Breathing room above and below, for a box with no fixed height. */
  padded?: boolean | undefined;
  children: ReactNode;
}

/**
 * Break glass: the fixed slot a logo sits in.
 *
 * The point of the box is that its pixel size is the same for every carrier or
 * bank, whatever shape their mark is, so neighbouring rows line up — which means
 * a runtime pixel width and height rather than a spacing token. `Row` can do the
 * alignment but not the fixed geometry.
 */
export function IconsLogoBox({
  align,
  width,
  height,
  labelGap,
  padded,
  children,
}: IconsLogoBoxProps) {
  const box: CSSProperties = {
    ...(width !== undefined && { width: `${width}px` }),
    ...(height !== undefined && { height: `${height}px` }),
  };

  const classes = [
    "flex flex-row items-center",
    justifyClassFor[align ?? "left"],
    labelGap ? "gap-3" : "",
    padded ? "py-2" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={classes} style={box}>
      {children}
    </div>
  );
}
